import { ICandidateDetails, TReopenStageData } from "../../../../store/superadmin/types";
import { isStageReopened } from "../../../../utils/stageHelper";

export type StageInfoStatus =
  | 'in-progress'
  | 'submitted'
  | 'reopened'
  | 'resubmitted'
  | 'admin-complete';

export interface StageInfo {
  status: StageInfoStatus;
  startedDate?: string;
  reopenedDate?: string;
  submittedDate?: string;
}

/**
 * Get the status and dates for each stage based on the candidate's profile data.
 *
 * Handles the following flow:
 * Normal Flow:   pending → waiting → approved → completed
 * Reopening Flow: approved → reopened → waiting (after resubmission) → approved
 * Multiple Stages: approved → reopened (stages [1, 2]) → waiting → approved
 *
 * @param reviewCandidateData - The full candidate details object, or null/undefined.
 * @param reopenedStagesStatus - The list of reopened-stage records from Redux state.
 * @param stageNumber - The stage number to evaluate (1, 2, or 3).
 * @returns A StageInfo object, or null if the stage hasn't been reached yet.
 */
export const getStageInfo = (
  reviewCandidateData: ICandidateDetails | null | undefined,
  reopenedStagesStatus: TReopenStageData[] | null | undefined,
  stageNumber: number
): StageInfo | null => {
  const profile = reviewCandidateData?.profile; 
  const stageApprovalStatus = reviewCandidateData?.stage_approval_status;
  const stageKey = `stage_${stageNumber}` as 'stage_1' | 'stage_2' | 'stage_3';
  const stageData = stageApprovalStatus?.[stageKey];  

  // Check if this specific stage is reopened
  const isReopened = reviewCandidateData
    ? isStageReopened(reviewCandidateData, stageNumber)
    : false;

  // Check current review status
  const reviewStatus = profile?.review_status;
  const currentProfileStage = profile?.current_stage;
  const currentReopendedData = reopenedStagesStatus?.find(
    (reop) => reop.stage_number === stageNumber && reop.status === 'reopened'
  );

  const currentResubmittedData = reopenedStagesStatus?.find(
    (reop) => reop.stage_number === stageNumber && reop.status === 'resubmitted'
  );

  

  if (stageNumber === 3) { 
    // 1. Reopened → in-progress
    if (isReopened) { 
      return {
        status: 'in-progress',
        startedDate: undefined,
        reopenedDate: currentReopendedData?.reopened_at ?? undefined,
        submittedDate: undefined,
      };
    }

    // 2. Resubmitted
    if (currentResubmittedData?.stage_number === 3 && currentResubmittedData?.status === 'resubmitted') {
      return {
        status: 'resubmitted',
        reopenedDate: undefined,
        submittedDate: currentResubmittedData?.resubmitted_at ?? undefined,
        startedDate: undefined,
      };
    }

    if(isReopened === false &&  currentProfileStage === 3 ) {     
        return {
          status: 'submitted',
          submittedDate: profile?.updated_at ?? profile?.created_at ?? undefined,
          reopenedDate: undefined,
          startedDate: undefined,
        };      
    }

    // 3. Submitted (approved)
    if (stageData && Array.isArray(stageData) && stageData.length > 0) {
      const latestAction = stageData.at(-1);
      if (latestAction?.status === 'approved') {
        return {
          status: 'submitted',
          submittedDate: (latestAction.updated_at || latestAction.created_at) ?? undefined,
          reopenedDate: undefined,
          startedDate: undefined,
        };
      }
    }
  }

  if (stageNumber === 2) { 
    const isAdmincomplete = reviewCandidateData?.reopened_stages_status?.find(
      (s) => s.stage_number === 2 && s.status === 'reopened'
    )?.sections?.includes('admin_complete');
    const isAdminCompleteFromProfile = profile?.profile_steps?.find(
      (step) => step.action === 'approve_candidate_bypass_compliance_check'
    )

    if(reviewCandidateData?.referoo_status === "complete" && !isReopened) {
      return {
        status: 'submitted',
        submittedDate: profile?.updated_at ?? profile?.created_at ?? undefined,
        reopenedDate: undefined,
        startedDate: undefined,
      };
    }
    
    // Check for admin-complete status (Stage 2 with bypass)
    if (  
      (stageNumber === 2 && isAdminCompleteFromProfile && isAdminCompleteFromProfile !== undefined ) ||
      (isAdmincomplete && isAdmincomplete !== undefined)
    ) { 
      const profilesteps = reviewCandidateData?.profile?.profile_steps;
      const adminCompleteStep = profilesteps?.find(
        (step) => step.action === 'approve_candidate_to_stage_3'
        && step.step_number === 2
      );
      const stageTwoApproved = adminCompleteStep ? (adminCompleteStep.updated_at || adminCompleteStep.created_at) : undefined;
      const stageTwoReopenedWithAdminComplete = currentReopendedData?.sections?.includes('admin_complete');
      const stageTwoReopenedWithCandidateComplete = currentReopendedData?.sections?.includes('candidate_complete');
     

      if(((stageTwoReopenedWithAdminComplete || isAdminCompleteFromProfile) && !stageTwoApproved) || (stageTwoReopenedWithAdminComplete)) { 
        return {
          status: 'admin-complete', 
          startedDate: profile?.updated_at ?? undefined,
          reopenedDate: undefined,
          submittedDate: undefined,
        };
      }

      if(stageTwoReopenedWithCandidateComplete) {
        return {
          status: 'in-progress',
          startedDate: undefined,
          reopenedDate: currentReopendedData?.reopened_at ?? undefined,
          submittedDate: undefined,
        };
      }

      if(stageTwoApproved) {
        return {
          status: 'submitted',
          submittedDate: profile?.updated_at ?? profile?.created_at ?? undefined,
          reopenedDate: undefined,
          startedDate: undefined,
        };
      }
      
    }

  
  }

  if (reviewStatus === null && stageKey === 'stage_1') {
    return {
      status: 'in-progress',
      startedDate: profile?.created_at ?? undefined,
      reopenedDate: undefined,
      submittedDate: undefined,
    };
  }

  // Priority 1: Check if this stage is REOPENED
  if (isReopened && reviewStatus === 'reopened' && stageNumber !== 2) {
    return {
      status: 'reopened',
      reopenedDate: profile?.reopened_at ?? undefined,
      submittedDate: undefined,
      startedDate: undefined,
    };
  }

  if (
    currentReopendedData?.stage_number === stageNumber &&
    currentReopendedData?.status !== 'resubmitted'
  ) {
    return {
      status: 'reopened',
      reopenedDate: currentReopendedData?.reopened_at ?? undefined,
      submittedDate: undefined,
      startedDate: undefined,
    };
  }

  if (
    currentResubmittedData?.stage_number === stageNumber &&
    currentResubmittedData?.status === 'resubmitted'
  ) {
    return {
      status: 'resubmitted',
      reopenedDate: undefined,
      submittedDate: currentResubmittedData?.resubmitted_at ?? undefined,
      startedDate: undefined,
    };
  }

  // Priority 2: Check if stage was reopened and now RESUBMITTED (waiting for approval)
  if (reviewStatus === 'waiting' && currentProfileStage === stageNumber) {
    return {
      status: 'submitted',
      startedDate: undefined,
      reopenedDate: undefined,
      submittedDate: profile?.updated_at ?? profile?.created_at ?? undefined,
    };
  }

  // Priority 3: Check if this stage has been APPROVED (submitted)
  if (stageData && Array.isArray(stageData) && stageData.length > 0) {
    const latestAction = stageData.at(-1);

    if (latestAction?.status === 'approved') {
      return {
        status: 'submitted',
        submittedDate:
          (latestAction.updated_at || latestAction.created_at) ?? undefined,
        reopenedDate: undefined,
        startedDate: undefined,
      };
    }
  }

  // Priority 5: Check if this is the NEXT stage after an approved stage
  // Show next stage as "in-progress" if previous stage is approved
  if (stageNumber > 1 && stageNumber <= 3) {
    const previousStageKey = `stage_${stageNumber - 1}` as
      | 'stage_1'
      | 'stage_2'
      | 'stage_3';
    const previousStageData = stageApprovalStatus?.[previousStageKey];

    if (
      previousStageData &&
      Array.isArray(previousStageData) &&
      previousStageData.length > 0
    ) {
      const latestPreviousAction = previousStageData.at(-1);

      if(reviewCandidateData?.referoo_status === "complete" && stageNumber === 2 && !isReopened) {
        return {
          status: 'submitted',
          submittedDate:
            latestPreviousAction ? (latestPreviousAction.updated_at ||
              latestPreviousAction.created_at) : undefined,
          reopenedDate: undefined,
          startedDate: undefined,
        };
      }

      // If previous stage is approved, show this stage as in-progress
      if (latestPreviousAction?.status === 'approved') {
        return {
          status: 'in-progress', 
          startedDate:
            (latestPreviousAction.updated_at ||
              latestPreviousAction.created_at) ?? undefined,
          reopenedDate: undefined,
          submittedDate: undefined,
        };
      }
    }
  }

  if(reviewCandidateData?.profile?.id && stageNumber === 1 && reviewCandidateData?.profile?.current_stage === null) {
      return {
          status: 'in-progress', 
          startedDate: reviewCandidateData.profile.created_at ?? undefined,
          reopenedDate: undefined,
          submittedDate: undefined,
        };
  }

  // Stage hasn't been reached yet
  return null;
};